// src/utils/searchRelevance.js
//
// Shared helpers for making transcript search results readable: find the part
// of a 500-word transcript chunk that is actually relevant to the user's query
// (so snippets don't just show the chunk's first 250 characters, which usually
// land mid-way through whoever spoke before the person the user asked about),
// and highlight query words / speaker labels without corrupting HTML that was
// already inserted (timestamp spans, other marks).
//
// Attributed transcripts have the shape "[H:MM:SS] President Koo: text…" — one
// label per speaker turn — which is what the speaker-anchor logic keys off.

const STOPWORDS = new Set([
  'the', 'and', 'for', 'that', 'this', 'these', 'those', 'with', 'from',
  'about', 'regarding', 'what', 'when', 'where', 'which', 'who', 'whom',
  'why', 'how', 'did', 'does', 'was', 'were', 'are', 'been', 'being',
  'have', 'has', 'had', 'will', 'would', 'can', 'could', 'should', 'shall',
  'say', 'says', 'said', 'their', 'they', 'she', 'her', 'him', 'his',
  'you', 'your', 'not', 'but', 'all', 'any', 'into', 'out', 'over',
]);

/** Meaningful lowercase tokens from a free-text query. */
export function queryTokens(term) {
  const words = String(term || '').toLowerCase().match(/[a-z0-9']+/g) || [];
  return [...new Set(words)].filter((w) => w.length >= 3 && !STOPWORDS.has(w));
}

// "[H:MM:SS] Some Speaker Label:" — group(1) = the label text.
const LABEL_RE = /\[\d{1,2}:\d{2}(?::\d{2})?\]\s*([A-Z][^:<\n[\]]{0,40}?):/g;

function labelMatchesTokens(label, tokens) {
  const words = String(label).toLowerCase().match(/[a-z']+/g) || [];
  return words.some((w) => tokens.includes(w));
}

function countTokenHits(lowerText, tokens, from, to) {
  let count = 0;
  for (const t of tokens) {
    let i = from;
    while ((i = lowerText.indexOf(t, i)) !== -1 && i < to) {
      count += 1;
      i += t.length;
    }
  }
  return count;
}

/**
 * Find the most relevant position in `content` for `term`.
 *
 * Priority:
 *  1. A speaker turn whose label names someone in the query ("Koo" ->
 *     "[2:22:28] President Koo:"), scored by how many other query words appear
 *     in the 400 characters that follow (so the Koo turn about the queried
 *     topic beats her "thank you" turn).
 *  2. The densest cluster of query-word hits (topic-only queries).
 *
 * Returns { index, type: 'speaker' | 'keyword' } or null. For 'speaker' the
 * index points at the "[H:MM:SS]" marker that precedes the label, so a snippet
 * starting there reads "[2:22:28] President Koo: …".
 */
export function findRelevantSegment(content, term) {
  const text = String(content || '');
  const tokens = queryTokens(term);
  if (!text || !tokens.length) return null;
  const lower = text.toLowerCase();

  // 1) speaker-label anchor
  LABEL_RE.lastIndex = 0;
  let best = null;
  let bestScore = -1;
  let m;
  while ((m = LABEL_RE.exec(text)) !== null) {
    if (!labelMatchesTokens(m[1], tokens)) continue;
    const start = m.index + m[0].length;
    const score = countTokenHits(lower, tokens, start, start + 400);
    if (score > bestScore) {
      bestScore = score;
      best = { index: m.index, type: 'speaker' };
    }
  }
  if (best) return best;

  // 2) keyword-density window
  const hits = [];
  for (const t of tokens) {
    let i = 0;
    while ((i = lower.indexOf(t, i)) !== -1) {
      hits.push(i);
      i += t.length;
    }
  }
  if (!hits.length) return null;
  hits.sort((a, b) => a - b);
  let bestHit = hits[0];
  let bestCount = 0;
  for (let a = 0; a < hits.length; a++) {
    let count = 0;
    for (let b = a; b < hits.length && hits[b] - hits[a] <= 300; b++) count += 1;
    if (count > bestCount) {
      bestCount = count;
      bestHit = hits[a];
    }
  }
  return { index: bestHit, type: 'keyword' };
}

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Wrap query matches in <mark class="{className}"> without touching text that
 * is inside an HTML tag (attribute values, class names). Highlights the full
 * phrase when it appears verbatim, plus each meaningful query word.
 */
export function markQueryMatches(html, term, className) {
  const tokens = queryTokens(term);
  const phrase = String(term || '').trim();
  const patterns = [];
  if (phrase.length >= 3) patterns.push(escapeRegex(phrase));
  for (const t of tokens) patterns.push(`\\b${escapeRegex(t)}\\b`);
  if (!patterns.length) return html;

  const re = new RegExp(`(<[^>]*>)|(${patterns.join('|')})`, 'gi');
  return String(html).replace(re, (match, tag) =>
    tag ? tag : `<mark class="${className}">${match}</mark>`
  );
}

/**
 * Wrap "[H:MM:SS] Label:" speaker labels whose label names someone in the
 * query with <mark class="{className}">. Runs on text/HTML where timestamps
 * have NOT yet been converted to spans.
 */
export function markSpeakerLabels(html, term, className) {
  const tokens = queryTokens(term);
  if (!tokens.length) return html;
  const re = /(\[\d{1,2}:\d{2}(?::\d{2})?\]\s*)([A-Z][^:<\n[\]]{0,40}?)(:)/g;
  return String(html).replace(re, (match, ts, label, colon) =>
    labelMatchesTokens(label, tokens)
      ? `${ts}<mark class="${className}">${label}</mark>${colon}`
      : match
  );
}

/**
 * End offset for a "relevant passage" band that starts at `index`: run to the
 * next speaker-turn marker after at least `minLen` characters, capped at
 * `maxLen`, snapping to the string end when nothing follows.
 */
export function passageEnd(content, index, minLen = 250, maxLen = 450) {
  const text = String(content || '');
  const searchFrom = index + minLen;
  if (searchFrom >= text.length) return text.length;
  const nextMarker = text.slice(searchFrom).search(/\[\d{1,2}:\d{2}(?::\d{2})?\]/);
  if (nextMarker === -1) return Math.min(text.length, index + maxLen);
  return Math.min(searchFrom + nextMarker, index + maxLen);
}
